import {
    ImageData,
    ImageListResponse,
    ImageFilters,
    QueryImageDto,
    UpdateImageRequest,
} from "../../types/image";
import { AIGCPIPLINEAPI } from "../apis/aigc";
import { apiWrapper } from "../request";

// Helper function to process tags - convert comma-separated string to array
const processTags = (tags: string[] | string): string[] => {
    if (Array.isArray(tags)) {
        // If it's already an array, process each item to split by comma
        return tags.flatMap((tag) => {
            if (typeof tag === "string") {
                return tag.split(",").map((t) => t.trim()).filter((t) => t.length > 0);
            }
            return [];
        });
    }
    if (typeof tags === "string") {
        return tags.split(",").map((t) => t.trim()).filter((t) => t.length > 0);
    }
    return [];
};

// Get filtered image list with keyword search and pagination
export const fetchImageList = async (
    filters?: ImageFilters,
    pageNum?: number,
    pageSize?: number,
): Promise<ImageListResponse> => {
    const queryData: QueryImageDto = {
        query: filters?.keyword || "",
        style: filters?.style === "" ? "" : (filters?.style || ""),
        pageIndex: pageNum || 1, // pageIndex从1开始
        pageSize: pageSize || 20,
    };

    const response = await apiWrapper({
        config: {
            url: AIGCPIPLINEAPI.imageList_POST,
            method: "POST",
            data: queryData,
        },
    });

    if (response) {
        // New schema: PagedResult<SearchResult<Image>>
        // Detect SearchResult wrapper where each item has an `item` property
        if (response.items && Array.isArray(response.items)) {
            const first = response.items[0];
            if (first && first.document !== undefined) {
                const imageList = response.items.map((sr: any) => {
                    const img = sr.document || {};
                    return {
                        ...img,
                        // normalize tags to array
                        tags: processTags(img.tags || []),
                        // attach search metadata if present
                        score: sr.score ?? null,
                        rerankerScore: sr.semanticSearch?.rerankerScore ?? null,
                    };
                });

                return {
                    images: imageList,
                    total: response.totalCount,
                    page: pageNum || 1,
                    pageSize: pageSize || 20,
                };
            }
        }

        // 检查后端返回的数据结构
        if (Array.isArray(response)) {
            // 如果返回数组格式，说明是旧版本响应，使用数组长度作为total
            const processedImages = response.map((item: any) => ({
                ...item,
                tags: processTags(item.tags),
            }));

            return {
                images: processedImages,
                total: processedImages.length,
                page: pageNum || 1,
                pageSize: pageSize || 20,
            };
        }

        // 如果返回对象格式，检查是否包含totalCount字段
        if (response.totalCount !== undefined) {
            // 新版本响应格式，包含totalCount
            // 检查是否使用 items 字段而不是 images 字段
            const imageList = response.items || response.images || [];
            const processedResponse = {
                images: imageList.map((item: any) => ({
                    ...item,
                    tags: processTags(item.tags),
                })),
                total: response.totalCount, // 使用totalCount作为总数
                totalCount: response.totalCount,
                page: pageNum || 1,
                pageSize: pageSize || 20,
            };
            return processedResponse;
        }

        // 兼容旧版本响应格式
        const processedResponse = {
            ...response,
            images: response.images?.map((item: any) => ({
                ...item,
                tags: processTags(item.tags),
            })) || [],
            total: response.total || (response.images?.length || 0),
        };
        return processedResponse;
    }
    throw new Error("Failed to fetch image list");
};

// Delete image by ID
export const deleteImage = async (imageId: string): Promise<boolean> => {
    try {
        // 添加调试日志
        console.log("Deleting image with ID:", imageId);

        const response = await apiWrapper({
            config: {
                url: AIGCPIPLINEAPI.imageOperationsUrl, // 使用相同的端点，但用DELETE方法
                method: "DELETE",
                params: { id: imageId },
            },
        });

        console.log("Delete response:", response);
        return true;
    } catch (error: any) {
        console.error("Delete image error:", error);

        // 提供更详细的错误信息
        if (error.response && error.response.status === 404) {
            throw new Error(`Image with ID "${imageId}" not found. Please check if the image exists.`);
        } else if (error.response && error.response.status) {
            throw new Error(`Failed to delete image: ${error.response.status} - ${error.response.statusText}`);
        } else {
            throw new Error(`Network error while deleting image: ${error.message || "Unknown error"}`);
        }
    }
};

// Get image URL by ID
export const getImageUrl = async (imageId: string): Promise<string> => {
    const response = await apiWrapper({
        config: {
            url: AIGCPIPLINEAPI.imageGetUrl_GET,
            method: "GET",
            pathVars: { imageId },
        },
    });

    if (response && response.url) {
        return response.url;
    }
    return "";
};

// Get image information by ID
export const getImageById = async (imageId: string): Promise<ImageData | null> => {
    try {
        const response = await apiWrapper({
            config: {
                url: AIGCPIPLINEAPI.imageOperationsUrl,
                method: "GET",
                params: { imageId },
            },
        });

        if (response) {
            // 处理 tags 格式，确保返回的是数组
            const processedImage = {
                ...response,
                tags: processTags(response.tags || []),
            };
            return processedImage;
        }
        return null;
    } catch (error: any) {
        console.error("Get image by ID error:", error);

        if (error.response && error.response.status === 404) {
            throw new Error(`Image with ID "${imageId}" not found.`);
        } else if (error.response && error.response.status) {
            throw new Error(`Failed to get image: ${error.response.status} - ${error.response.statusText}`);
        } else {
            throw new Error(`Network error while getting image: ${error.message || "Unknown error"}`);
        }
    }
};

// Get image URLs by ID (returns array of URLs)
export const getImageUrls = async (imageId: string): Promise<string[]> => {
    const response = await apiWrapper({
        config: {
            url: AIGCPIPLINEAPI.imageGetUrl_GET,
            method: "GET",
            pathVars: { imageId },
        },
    });

    if (response && Array.isArray(response)) {
        return response;
    }
    if (response && response.urls && Array.isArray(response.urls)) {
        return response.urls;
    }
    return [];
};

// Upload new image
export const uploadImage = async (
    imageData: UpdateImageRequest,
): Promise<ImageData> => {
    try {
        // 创建 FormData 来处理文件上传
        const formData = new FormData();

        // 添加 CreateImageDto 字段 - 匹配后端结构
        formData.append("image.imageId", imageData.imageId);
        formData.append("image.style", imageData.style);
        if (imageData.description) {
            formData.append("image.description", imageData.description);
        }

        // 添加图片数量 - 基于上传的文件数量
        formData.append("image.imageCount", imageData.files.length.toString());

        // 处理 tags - 后端期望 List<string>
        if (imageData.tags && imageData.tags.length > 0) {
            imageData.tags.forEach((tag: string, index: number) => {
                formData.append(`image.tags[${index}]`, tag);
            });
        }

        if (imageData.rating !== null && imageData.rating !== undefined) {
            formData.append("image.rating", imageData.rating.toString());
        }

        // 添加文件名数组 - 传递给后台 CreateImageDto 的 FileNames 属性
        if (imageData.files && imageData.files.length > 0) {
            imageData.files.forEach((file: File, index: number) => {
                formData.append(`image.fileNames[${index}]`, file.name);
            });
        }

        // 添加文件 - 后端参数名是 imageFiles
        imageData.files.forEach((file: File) => {
            formData.append("imageFiles", file);
        });

        console.log("Upload data prepared:");
        console.log("ImageId:", imageData.imageId);
        console.log("Style:", imageData.style);
        console.log("Description:", imageData.description);
        console.log("Tags:", imageData.tags);
        console.log("Rating:", imageData.rating);
        console.log("Files count:", imageData.files.length);

        // 使用 apiWrapper 替代 XMLHttpRequest
        const response = await apiWrapper({
            config: {
                url: AIGCPIPLINEAPI.imageOperationsUrl,
                method: "POST",
                data: formData,
                headers: {
                    // 让浏览器自动设置 Content-Type 为 multipart/form-data
                    "Content-Type": undefined,
                },
            },
        });

        if (response) {
            // 处理 tags 格式，确保返回的是数组
            const processedImage = {
                ...response,
                tags: processTags(response.tags || []),
            };
            return processedImage;
        }
        throw new Error("Upload failed - no response");
    } catch (error: any) {
        console.error("Upload image error:", error);

        // 特殊处理 404 "not found" 情况
        if (error.response && error.response.status === 404 && error.message && error.message.includes("not found")) {
            console.log("ImageId not found (404), this is expected for new image creation");
            // 对于新图片创建，404是正常的，返回成功响应
            return {
                imageId: imageData.imageId,
                description: imageData.description || "",
                style: imageData.style,
                tags: imageData.tags || [],
                rating: imageData.rating || null,
                imageCount: imageData.files.length,
                fileNames: imageData.files.map((file) => file.name),
            };
        }

        if (error.response && error.response.status === 400) {
            throw new Error(`Invalid upload data: ${error.response.data?.message || "Bad request"}`);
        } else if (error.response && error.response.status) {
            throw new Error(`Failed to upload image: ${error.response.status} - ${error.response.statusText}`);
        } else {
            throw new Error(`Network error while uploading image: ${error.message || "Unknown error"}`);
        }
    }
};

// Update existing image information (without files)
export const updateImageInfo = async (
    imageId: string,
    updateData: {
        description?: string;
        tags?: string[];
        rating?: number | null;
    },
): Promise<ImageData> => {
    try {
        const response = await apiWrapper({
            config: {
                url: AIGCPIPLINEAPI.imageOperationsUrl,
                method: "PUT",
                data: {
                    imageId,
                    ...updateData,
                },
            },
        });

        if (response) {
            // 处理 tags 格式，确保返回的是数组
            const processedImage = {
                ...response,
                tags: processTags(response.tags || []),
            };
            return processedImage;
        }
        throw new Error("Update failed - no response");
    } catch (error: any) {
        console.error("Update image info error:", error);

        if (error.response && error.response.status === 404) {
            throw new Error(`Image with ID "${imageId}" not found.`);
        } else if (error.response && error.response.status === 400) {
            throw new Error(`Invalid update data: ${error.response.data?.message || "Bad request"}`);
        } else if (error.response && error.response.status) {
            throw new Error(`Failed to update image: ${error.response.status} - ${error.response.statusText}`);
        } else {
            throw new Error(`Network error while updating image: ${error.message || "Unknown error"}`);
        }
    }
};

// Update existing image with file modifications (similar to upload logic)
export const updateImageWithFiles = async (
    imageId: string,
    updateData: {
        description: string;
        style: string;
        tags: string[];
        rating?: number | null;
        newFiles?: File[];
        deletedImageIndices?: number[];
        currentImageCount?: number;
        originalFileNames?: string[];
    },
): Promise<ImageData> => {
    try {
        // 创建 FormData 来处理文件上传和更新
        const formData = new FormData();

        // 添加基本的更新字段
        formData.append("image.imageId", imageId);
        formData.append("image.style", updateData.style);
        formData.append("image.description", updateData.description);

        // 处理 tags - 后端期望 List<string>
        if (updateData.tags && updateData.tags.length > 0) {
            updateData.tags.forEach((tag: string, index: number) => {
                formData.append(`image.tags[${index}]`, tag);
            });
        }

        if (updateData.rating !== null && updateData.rating !== undefined) {
            formData.append("image.rating", updateData.rating.toString());
        }

        // 计算当前界面上显示的图片总数量（这是用户看到的最终图片数量）
        const originalImageCount = updateData.currentImageCount || 0;
        const deletedCount = updateData.deletedImageIndices?.length || 0;
        const addedCount = updateData.newFiles?.length || 0;
        const finalImageCount = originalImageCount - deletedCount + addedCount;

        // imageCount 应该等于界面上显示的图片总数
        formData.append("image.imageCount", finalImageCount.toString());

        // 计算修改后的文件名数组
        const originalFileNames = updateData.originalFileNames || [];
        const deletedIndices = new Set(updateData.deletedImageIndices || []);

        // 过滤掉被删除的文件名
        const remainingFileNames = originalFileNames.filter((_, index) => !deletedIndices.has(index));

        // 添加新上传的文件名
        const newFileNames = updateData.newFiles?.map((file) => file.name) || [];

        // 合并得到最终的文件名数组
        const finalFileNames = [...remainingFileNames, ...newFileNames];

        // 添加文件名数组到 FormData
        if (finalFileNames.length > 0) {
            finalFileNames.forEach((fileName: string, index: number) => {
                formData.append(`image.fileNames[${index}]`, fileName);
            });
        }

        // 添加删除的图片索引信息（如果有删除操作）
        if (updateData.deletedImageIndices && updateData.deletedImageIndices.length > 0) {
            updateData.deletedImageIndices.forEach((index: number, i: number) => {
                formData.append(`deletedImageIndices[${i}]`, index.toString());
            });
        }

        // 只有在用户新添加了本地图片时，imageFiles 才有内容，否则为空
        if (updateData.newFiles && updateData.newFiles.length > 0) {
            updateData.newFiles.forEach((file: File) => {
                formData.append("imageFiles", file);
            });
        }
        // 如果没有新添加的文件，imageFiles 参数会是空的，这是正确的

        console.log("Update data prepared:");
        console.log("ImageId:", imageId);
        console.log("Description:", updateData.description);
        console.log("Tags:", updateData.tags);
        console.log("Rating:", updateData.rating);
        console.log("Deleted indices:", updateData.deletedImageIndices);
        console.log("New files count:", updateData.newFiles?.length || 0);
        console.log("Final image count:", finalImageCount);
        console.log("Original file names:", originalFileNames);
        console.log("Final file names:", finalFileNames);

        // 使用 apiWrapper 替代 XMLHttpRequest，尝试使用 PUT 方法
        const response = await apiWrapper({
            config: {
                url: AIGCPIPLINEAPI.imageOperationsUrl,
                method: "PUT",
                data: formData,
                headers: {
                    // 让浏览器自动设置 Content-Type 为 multipart/form-data
                    "Content-Type": undefined,
                },
            },
        });

        if (response) {
            // 处理 tags 格式，确保返回的是数组
            const processedImage = {
                ...response,
                tags: processTags(response.tags || []),
            };
            return processedImage;
        }
        throw new Error("Update failed - no response");
    } catch (error: any) {
        console.error("Update image with files error:", error);

        // 如果是405方法不允许错误，尝试使用POST方法
        if (error.response && error.response.status === 405) {
            console.log("PUT method not allowed, trying POST method for update...");
            try {
                const formData = new FormData();

                // 重新创建FormData（因为FormData不能重复使用）
                formData.append("image.imageId", imageId);
                formData.append("image.style", updateData.style);
                formData.append("image.description", updateData.description);

                if (updateData.tags && updateData.tags.length > 0) {
                    updateData.tags.forEach((tag: string, index: number) => {
                        formData.append(`image.tags[${index}]`, tag);
                    });
                }

                if (updateData.rating !== null && updateData.rating !== undefined) {
                    formData.append("image.rating", updateData.rating.toString());
                }

                const originalImageCount = updateData.currentImageCount || 0;
                const deletedCount = updateData.deletedImageIndices?.length || 0;
                const addedCount = updateData.newFiles?.length || 0;
                const finalImageCount = originalImageCount - deletedCount + addedCount;

                formData.append("image.imageCount", finalImageCount.toString());

                // 计算修改后的文件名数组（POST重试逻辑）
                const originalFileNames = updateData.originalFileNames || [];
                const deletedIndices = new Set(updateData.deletedImageIndices || []);

                // 过滤掉被删除的文件名
                const remainingFileNames = originalFileNames.filter((_, index) => !deletedIndices.has(index));

                // 添加新上传的文件名
                const newFileNames = updateData.newFiles?.map((file) => file.name) || [];

                // 合并得到最终的文件名数组
                const finalFileNames = [...remainingFileNames, ...newFileNames];

                // 添加文件名数组到 FormData
                if (finalFileNames.length > 0) {
                    finalFileNames.forEach((fileName: string, index: number) => {
                        formData.append(`image.fileNames[${index}]`, fileName);
                    });
                }

                if (updateData.deletedImageIndices && updateData.deletedImageIndices.length > 0) {
                    updateData.deletedImageIndices.forEach((index: number, i: number) => {
                        formData.append(`deletedImageIndices[${i}]`, index.toString());
                    });
                }

                if (updateData.newFiles && updateData.newFiles.length > 0) {
                    updateData.newFiles.forEach((file: File) => {
                        formData.append("imageFiles", file);
                    });
                }

                // 使用POST方法重试
                const retryResponse = await apiWrapper({
                    config: {
                        url: AIGCPIPLINEAPI.imageOperationsUrl,
                        method: "POST",
                        data: formData,
                        headers: {
                            "Content-Type": undefined,
                        },
                    },
                });

                if (retryResponse) {
                    const processedImage = {
                        ...retryResponse,
                        tags: processTags(retryResponse.tags || []),
                    };
                    return processedImage;
                }
                throw new Error("Update with POST failed - no response");
            } catch (retryError: any) {
                console.error("POST retry also failed:", retryError);
                throw new Error(`Update failed: Both PUT (405) and POST methods failed. ${retryError.message}`);
            }
        }

        if (error.response && error.response.status === 404) {
            throw new Error(`Image with ID "${imageId}" not found.`);
        } else if (error.response && error.response.status === 400) {
            throw new Error(`Invalid update data: ${error.response.data?.message || "Bad request"}`);
        } else if (error.response && error.response.status) {
            throw new Error(`Failed to update image: ${error.response.status} - ${error.response.statusText}`);
        } else {
            throw new Error(`Network error while updating image: ${error.message || "Unknown error"}`);
        }
    }
};

// Generate mock data for development/testing
export const generateMockImageData = (count: number = 10): ImageData[] => Array.from({ length: count }, (_, i) => ({
    imageId: `img-${i + 1}`,
    description: `Sample image ${i + 1} description`,
    style: ["modern", "classic", "abstract", "realistic"][i % 4],
    tags: [`tag${i + 1}`, `category${(i % 3) + 1}`],
    rating: Math.random() > 0.3 ? Math.floor(Math.random() * 5) + 1 : null,
    imageCount: Math.floor(Math.random() * 5) + 1,
    fileNames: [`image_${i + 1}_1.jpg`, `image_${i + 1}_2.png`],
}));

// Helper functions for image operations
export const validateImageId = (imageId: string): boolean => imageId.trim().length > 0;

export const validateImageData = (data: UpdateImageRequest): boolean => data.description.trim().length > 0 && data.style.trim().length > 0;

export const formatImageData = (data: ImageData): ImageData => ({
    ...data,
    description: data.description.trim(),
    style: data.style.trim(),
    tags: data.tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0),
});
